import * as moment from 'moment';
import { isString, get } from 'lodash';
import * as luhn from 'luhn';
import {DATE_FORMAT} from './helpers';

type ValidatorReturn = any | null;
type DateInput = string | moment.Moment;

const isRequired = (value): ValidatorReturn => {
  // An exact false is an answered question
  // checking something isn't false should be another validator
  if (value === false) {
    return null;
  }

  return !value || value.length === 0 ? 'Oops – please answer this question' : null;
};

const isRequiredAtleastOne = (fields, values) => (value: any): ValidatorReturn => {
  for (const field of fields) {
    const val = get(values, field.name);
    if (val === true) {
      return undefined;
    }
  }
  return 'The field is required';
};

const isNotFalse = (value): ValidatorReturn => {
  if (value) {
    return null;
  }

  return 'The field is required';
};

const parseDate = (date: DateInput): moment.Moment => {
  if (!isString(date)) {
    return date;
  }

  return moment(date, DATE_FORMAT);
};

const isValidDate = (value: string): ValidatorReturn => {
  let valid = null;

  if (value.length !== DATE_FORMAT.length) {
    valid = 'Invalid date';
  }

  if (!parseDate(value).isValid()) {
    valid = 'Invalid date';
  }

  return valid;
};

const isValidDob = (value: string): ValidatorReturn => {
  let valid = null;

  if (value.length !== DATE_FORMAT.length) {
    valid = 'Oops – please check your date of birth';
  }

  if (!parseDate(value).isValid()) {
    valid = 'Oops – please check your date of birth';
  }

  return valid;
};

const isBefore = (date: DateInput, message: string = null) => (value: string): ValidatorReturn => {
  date = parseDate(date);
  const m = message ? message : 'Must be before ' + date.format(DATE_FORMAT);
  const momentValue = parseDate(value);

  return date.isBefore(momentValue) ? m : null;
};

const isAfter = (date: DateInput, message: string = null) => (value: string): ValidatorReturn => {
  date = parseDate(date);
  const m = message ? message : 'Must be after ' + date.format(DATE_FORMAT);
  const momentValue = parseDate(value);

  return date.isAfter(momentValue) ? m : null;
};

const isFutureDate = (value: string) => {
  const date = moment().startOf('day');
  const momentValue = parseDate(value);

  return date.isSame(momentValue) ? null : isAfter(date, 'Date cannot be in the past')(value);
};

// tslint:disable:max-line-length
const isAdult = isBefore(moment().startOf('day').subtract(18, 'years'), 'Traveller must be an adult.');
const minAge = isBefore(moment().startOf('day').subtract(15, 'years'), 'Oops – please check your date of birth.');
const maxAge = isAfter(moment().startOf('day').subtract(100, 'years'), 'Oops – please check your date of birth.');
const isChild = isAfter(moment().startOf('day').subtract(18, 'years'), 'Child must be under 18.');
const isDobAFutureDate =
  isBefore(moment().startOf('day').subtract(0, 'day'), 'Traveller\'s age cannot be in the future.');

// Regex from http://emailregex.com/
const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const isEmail = (value: string): ValidatorReturn => {
  return !emailRegex.test(value) ? 'Email address is invalid' : null;
};

const annualLimit = isBefore(moment().startOf('day').subtract(-45, 'day'), 'Your policy cannot start more than 45 days in the future.');
const singleLimit = isBefore(moment().startOf('day').subtract(-365, 'day'), 'Your trip cannot start more than 365 days in the future.');
const minTripDuration = (value) => isAfter(moment(parseDate(value)).startOf('day').subtract(-2, 'day'), 'Your trip must be longer than 2 days.');
// const singleMaxTripDuration = isBefore(moment().startOf('day').subtract(-1, 'day'), 'Your trip must be longer than 1 day.');

const singleMaxTripDuration = (value: string) => {
  const date = moment().startOf('day').subtract(-90, 'day');
  const momentValue = parseDate(value);

  return date.isAfter(momentValue) ? isBefore(moment().startOf('day').subtract(-365, 'day'), 'Your trip cannot end after 365 days from today.') : isBefore(moment().startOf('day').subtract(-455, 'day'), 'Your trip cannot end after 455 days from today.');
};

const phoneRegex = /^[+]?[0-9]+$/;

const isPhone = (value: string): ValidatorReturn => {
  return !phoneRegex.test(value) || (value).length < 6 || (value).length > 12 ? 'Invalid phone number' : null;
};

const postcodeRegex = /([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9]?[A-Za-z]))))\s?[0-9][A-Za-z]{2})/;

const isPostcode = (value: string): ValidatorReturn => {
  return !postcodeRegex.test(value) ? 'Oops – this is an invalid postcode' : null;
};

const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*?()\\\-_=+{};:,<.>])[A-Za-z\d$!@#$%^&*?()\\\-_=+{};:,<.>]{10,}/;

const isValidPassword = (value: string): ValidatorReturn => {
  return !passwordRegex.test(value) ? 'Password is invalid' : null;
};

const isSame = (expects: string, message = 'This value must be the same as %s') => (value: string): ValidatorReturn => {
  return expects === value ? null : message.replace(/%s/i, expects);
};

const doesNotStartWith = (items: string[], stripSpaces = false, message = 'This value cannot start with %s.') => (value: string): ValidatorReturn => {
  let valid = true;

  items.forEach((item) => {
    let checkAgainst = value;

    if (stripSpaces) {
      checkAgainst = value.replace(' ', '');
    }

    if (checkAgainst.toLocaleLowerCase().startsWith(item.toLocaleLowerCase())) {
      valid = false;
    }
  });

  return valid ? null : message.replace(/%s/i, items.join(', '));
};

const isNotLongerThan = (length: number, message = 'This value cannot be longer than %s characters.') =>
  (value: string): ValidatorReturn => {
    return value.length < length ? null : message.replace(/%s/i, length.toString());
  };

const isNotShorterThan = (length: number, message = 'This value cannot be shorter than %s characters.') =>
  (value: string): ValidatorReturn => {
    return value.length > length ? null : message.replace(/%s/i, length.toString());
  };

const isLengthBetween = ([ min, max ]: [ number, number ],
                         message = 'This value should be between %s and %s characters.') =>
  (value: string): ValidatorReturn => {
    const length = value.length;

    return length >= min && length <= max ? null : message.replace(/%s/i, [ min, max ].join(', '));
  };

const isLuhnHappy = (message = 'Invalid card number') => (value: string): ValidatorReturn => {
  const val = value.replace(/\s/g, '');

  return luhn.validate(val) ? null : message;
};

const expiryDateRegex = /^(0[1-9]|1[0-2])\/?([0-9]{4})$/;

const isValidExpiryDate = (value: string): ValidatorReturn => {
  return !expiryDateRegex.test(value) ? 'Expiry date must be in the format MM/YYYY' : null;
};

const normalizeCurrency = (value) => value && value.replace(/(?!-)[^0-9.]/g, '');

// function isNumeric(n) {
//   return !isNaN(parseFloat(n)) && isFinite(n);
// }

const isNumeric = (message = 'This value is not a number') => (value: string): ValidatorReturn => {
  return !isNaN(parseFloat(value)) && isFinite(parseFloat(value)) ? message : null;
};

const hasSameValue = (object1: object, object2: object, fieldName: string, index?: number) => {
  return object1.hasOwnProperty(fieldName) &&
    object2.hasOwnProperty(fieldName) &&
    object1[fieldName] === object2[fieldName];
};

const showErrorMessage = (show: boolean, message = '') => (value: string): ValidatorReturn => {
  return show && message ? message : null;
};

const filterCurrency = (value, separator = ',') => {
  if (!value) {
    return '';
  }
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, separator);
};

const filterNumber = (value, isFloat ?: boolean) => {
  if (! value) {
    return '';
  }

  const regex = isFloat ? /[^\d.-]/g : /\D/g;

  return value.toString().replace(regex, '');
};

const isGreaterThan = (greaterThan: number, message = '') => (value: string): ValidatorReturn => {
  return parseFloat(value) <= greaterThan ? message : '';
};

const normalizeYear = (value) => {
  if (!value) {
    return value;
  }

  const onlyNums = value.replace(/[^\d]/g, '');
  if (onlyNums.length <= 4) {
    return onlyNums;
  }
  return `${onlyNums.slice(0, 4)}`;
};

const validateDrivingRestriction = (driverCount: number) => (value: string): ValidatorReturn => {
  const drivingRestrictionsMinOneDriver = [
    'Named drivers',
    'Named drivers excluding Policyholder',
    'Any driver aged over 21 other than those named',
    'Any driver aged over 25 other than those named',
    'Any driver aged over 30 other than those named',
  ];

  const drivingRestrictionsMinTwoDriver = [
    'Insured and Spouse',
    'Insured and named',
  ];

  if (drivingRestrictionsMinOneDriver.includes(value) && driverCount < 1) {
    return 'Please provide at least one driver';
  }

  if (drivingRestrictionsMinTwoDriver.includes(value) && driverCount < 2) {
    return 'Please provide two drivers';
  }

  return null;
};

export {
  isRequired,
  isValidDate,
  isFutureDate,
  isAdult,
  minAge,
  maxAge,
  isChild,
  isDobAFutureDate,
  annualLimit,
  singleLimit,
  minTripDuration,
  singleMaxTripDuration,
  isAfter,
  isBefore,
  isEmail,
  isPhone,
  isNotFalse,
  isValidPassword,
  isSame,
  doesNotStartWith,
  isPostcode,
  isNotLongerThan,
  isNotShorterThan,
  isValidExpiryDate,
  isLengthBetween,
  isLuhnHappy,
  isNumeric,
  normalizeCurrency,
  hasSameValue,
  showErrorMessage,
  filterNumber,
  filterCurrency,
  isGreaterThan,
  isRequiredAtleastOne,
  normalizeYear,
  isValidDob,
  validateDrivingRestriction,
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/app/util/validators.ts